// ============================================================
// src/components/LessonView.jsx
// Vista completa de un tema: Leer → Practicar → Desafío
// ============================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, get, update, set } from 'firebase/database';
import { database } from '../services/firebase';

import fisicaData from '../data/fisica_openmind.json';
import quimicaData from '../data/quimica_openmind.json';
import biologiaData from '../data/biologia_openmind.json';
import historiaData from '../data/historia_openmind.json';
import politicaData from '../data/politica_openmind.json';
import arteData from '../data/arte_openmind.json';

const ALL_SUBJECTS = [
  { ...fisicaData, color: 'from-blue-500 to-cyan-500', accent: 'text-blue-600', ring: 'ring-blue-400' },
  { ...quimicaData, color: 'from-emerald-500 to-green-500', accent: 'text-emerald-600', ring: 'ring-emerald-400' },
  { ...biologiaData, color: 'from-purple-500 to-violet-500', accent: 'text-purple-600', ring: 'ring-purple-400' },
  { ...historiaData, color: 'from-amber-500 to-orange-500', accent: 'text-amber-600', ring: 'ring-amber-400' },
  { ...politicaData, color: 'from-red-500 to-rose-500', accent: 'text-red-600', ring: 'ring-red-400' },
  { ...arteData, color: 'from-pink-500 to-fuchsia-500', accent: 'text-pink-600', ring: 'ring-pink-400' },
];

const TABS = [
  { id: 'learn', label: '📖 Aprender' },
  { id: 'practice', label: '✏️ Practicar' },
  { id: 'challenge', label: '🚀 Desafío' },
];

export default function LessonView({ user }) {
  const { subject: subjectKey, level: levelIndex, topicId } = useParams();
  const navigate = useNavigate();

  const subject = ALL_SUBJECTS.find((s) => s.subject === subjectKey);
  const level = subject?.levels[parseInt(levelIndex)];
  const topic = level?.topics.find((t) => t.topicId === topicId);

  const [tab, setTab] = useState('learn');
  const [topicState, setTopicState] = useState({});
  const [mcqSelected, setMcqSelected] = useState({});
  const [mcqRevealed, setMcqRevealed] = useState({});
  const [shortAnswers, setShortAnswers] = useState({});
  const [shortRevealed, setShortRevealed] = useState({});
  const [challengeText, setChallengeText] = useState('');
  const [showCompletion, setShowCompletion] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || !topic) return;
    loadTopicState();
  }, [user, topicId]);

  const loadTopicState = async () => {
    const snap = await get(ref(database, `users/${user.uid}/progress/${subjectKey}/${topicId}`));
    if (snap.exists()) {
      const data = snap.val();
      setTopicState(data);
      setMcqSelected(data.mcqSelected || {});
      setMcqRevealed(data.mcqRevealed || {});
      setShortAnswers(data.shortAnswers || {});
      setShortRevealed(data.shortRevealed || {});
      setChallengeText(data.challengeAnswer || '');
      if (data.completed) setShowCompletion(true);
    }
  };

  if (!subject || !level || !topic) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">Tema no encontrado.</p>
      </div>
    );
  }

  // ── Puntos acumulados ─────────────────────────────────────
  const practicePoints = Object.values(topicState.practiceAnswers || {}).reduce(
    (acc, a) => acc + (a.points || 0), 0
  );
  const totalPoints = practicePoints + (topicState.challengeSubmitted ? 20 : 0);
  const progressPct = Math.round((totalPoints / 40) * 100);

  // ── Guardar en Firebase ───────────────────────────────────
  const saveToFirebase = async (updates) => {
    const path = `users/${user.uid}/progress/${subjectKey}/${topicId}`;
    await update(ref(database, path), updates);
    setTopicState((prev) => ({ ...prev, ...updates }));
  };

  // ── Responder opción múltiple ─────────────────────────────
  const handleMCQ = async (questionId, selectedIndex, correctIndex, points) => {
    if (mcqRevealed[questionId]) return;
    const isCorrect = selectedIndex === correctIndex;
    const earned = isCorrect ? points : 0;

    const newSelected = { ...mcqSelected, [questionId]: selectedIndex };
    const newRevealed = { ...mcqRevealed, [questionId]: true };
    const newAnswers = {
      ...(topicState.practiceAnswers || {}),
      [questionId]: { answer: selectedIndex, points: earned, answered: true },
    };

    setMcqSelected(newSelected);
    setMcqRevealed(newRevealed);

    await saveToFirebase({
      mcqSelected: newSelected,
      mcqRevealed: newRevealed,
      practiceAnswers: newAnswers,
    });
  };

  // ── Revelar respuesta corta ───────────────────────────────
  const handleShortReveal = async (questionId, points) => {
    if (shortRevealed[questionId]) return;
    const answer = shortAnswers[questionId] || '';
    const newRevealed = { ...shortRevealed, [questionId]: true };
    const newAnswers = {
      ...(topicState.practiceAnswers || {}),
      [questionId]: { answer, points, answered: true },
    };

    setShortRevealed(newRevealed);
    await saveToFirebase({
      shortAnswers: { ...(topicState.shortAnswers || {}), [questionId]: answer },
      shortRevealed: newRevealed,
      practiceAnswers: newAnswers,
    });
  };

  // ── Enviar desafío ────────────────────────────────────────
  const handleChallengeSubmit = async () => {
    if (!challengeText.trim() || topicState.challengeSubmitted) return;
    setSaving(true);

    const newPracticePoints = Object.values({
      ...(topicState.practiceAnswers || {}),
    }).reduce((acc, a) => acc + (a.points || 0), 0);
    const finalScore = newPracticePoints + 20;

    const updates = {
      challengeAnswer: challengeText,
      challengeSubmitted: true,
      completed: true,
      score: finalScore,
      completedAt: new Date().toISOString(),
    };

    await saveToFirebase(updates);

    // Actualizar stats globales del usuario
    await updateGlobalStats(finalScore);

    setShowCompletion(true);
    setSaving(false);
  };

  // ── Actualizar estadísticas globales ─────────────────────
  const updateGlobalStats = async (earnedPoints) => {
    const statsRef = ref(database, `users/${user.uid}/stats`);
    const snap = await get(statsRef);
    const current = snap.exists() ? snap.val() : { totalPoints: 0, level: 1, completedTopics: 0 };

    const newTotal = (current.totalPoints || 0) + earnedPoints;
    const newLevel = Math.floor(newTotal / 200) + 1;
    const newCompleted = (current.completedTopics || 0) + 1;

    await set(statsRef, {
      ...current,
      totalPoints: newTotal,
      level: newLevel,
      completedTopics: newCompleted,
      lastActive: new Date().toISOString(),
    });
  };

  // ── Siguiente tema ────────────────────────────────────────
  const allTopics = subject.levels.flatMap((l, li) =>
    l.topics.map((t) => ({ ...t, levelIndex: li }))
  );
  const currentIdx = allTopics.findIndex((t) => t.topicId === topicId);
  const nextTopic = allTopics[currentIdx + 1];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className={`bg-gradient-to-r ${subject.color} text-white px-4 py-6`}>
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate(`/subject/${subjectKey}`)}
            className="text-white/70 hover:text-white text-sm mb-3 flex items-center gap-1"
          >
            ← {subject.displayName} · Nivel {level.levelNumber}
          </button>
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-2xl md:text-3xl font-extrabold">{topic.title}</h1>
            <div className="bg-white/20 rounded-2xl px-4 py-2 text-center flex-shrink-0">
              <div className="text-2xl font-extrabold">{totalPoints}</div>
              <div className="text-white/70 text-xs">/ 40 pts</div>
            </div>
          </div>
          {/* Barra de progreso del tema */}
          <div className="mt-3 bg-white/20 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* ── Pestañas ───────────────────────────────────────── */}
      <div className="bg-white border-b sticky top-14 z-10 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 flex">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition
                ${tab === t.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Contenido ──────────────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* Banner de completado */}
        {showCompletion && (
          <div className={`mb-6 bg-gradient-to-r ${subject.color} text-white rounded-3xl p-6 text-center shadow-lg`}>
            <div className="text-5xl mb-2">🎉</div>
            <h2 className="text-2xl font-extrabold mb-1">¡Tema completado!</h2>
            <p className="text-white/80 mb-4">Obtuviste {totalPoints} de 40 puntos</p>
            {nextTopic ? (
              <button
                onClick={() => navigate(`/lesson/${subjectKey}/${nextTopic.levelIndex}/${nextTopic.topicId}`)}
                className="bg-white text-indigo-600 font-bold px-6 py-2 rounded-xl hover:bg-indigo-50 transition"
              >
                Siguiente tema →
              </button>
            ) : (
              <button
                onClick={() => navigate(`/subject/${subjectKey}`)}
                className="bg-white text-indigo-600 font-bold px-6 py-2 rounded-xl hover:bg-indigo-50 transition"
              >
                Ver materia completa 🏆
              </button>
            )}
          </div>
        )}

        {/* ── TAB: APRENDER ─────────────────────────────── */}
        {tab === 'learn' && (
          <div className="space-y-5">
            {/* Explicación */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="bg-indigo-50 border-b border-indigo-100 px-5 py-3">
                <h2 className="font-bold text-indigo-800">Explicación</h2>
              </div>
              <div className="px-5 py-5 space-y-4">
                {topic.content.explanation.split('\n\n').map((p, i) => (
                  <p key={i} className="text-gray-700 leading-relaxed">{p}</p>
                ))}
              </div>
            </div>

            {/* Puntos clave */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="bg-amber-50 border-b border-amber-100 px-5 py-3">
                <h2 className="font-bold text-amber-800">✨ Puntos clave</h2>
              </div>
              <div className="px-5 py-4 space-y-2">
                {topic.content.keyPoints.map((point, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-amber-400 mt-1 flex-shrink-0">●</span>
                    <p className="text-gray-700 text-sm">{point}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Ejemplos */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="bg-green-50 border-b border-green-100 px-5 py-3">
                <h2 className="font-bold text-green-800">🌍 Ejemplos reales</h2>
              </div>
              <div className="px-5 py-4 space-y-3">
                {topic.content.examples.map((ex, i) => (
                  <div key={i} className="bg-green-50 border border-green-100 rounded-xl p-4">
                    <p className="text-gray-700 text-sm leading-relaxed">{ex}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Botón continuar */}
            <button
              onClick={() => setTab('practice')}
              className={`w-full py-4 rounded-2xl font-bold text-white text-lg bg-gradient-to-r ${subject.color} hover:opacity-90 transition shadow-md`}
            >
              Practicar ahora →
            </button>
          </div>
        )}

        {/* ── TAB: PRACTICAR ────────────────────────────── */}
        {tab === 'practice' && (
          <div className="space-y-6">
            <p className="text-center text-gray-500 text-sm">
              Responde con tus propias palabras. El aprendizaje real viene de pensar, no de copiar.
            </p>

            {topic.practice.map((q, qi) => {
              const isAnswered = topicState.practiceAnswers?.[q.questionId]?.answered;

              return (
                <div key={q.questionId} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="bg-gray-50 border-b px-5 py-3 flex justify-between items-center">
                    <span className="font-semibold text-gray-700 text-sm">
                      Pregunta {qi + 1} de {topic.practice.length}
                    </span>
                    <span className="text-xs text-gray-400">
                      {q.points} pts · {q.type === 'multiple_choice' ? 'Opción múltiple' : 'Respuesta abierta'}
                    </span>
                  </div>
                  <div className="px-5 py-5">
                    <p className="font-medium text-gray-800 mb-4 leading-relaxed">{q.question}</p>

                    {/* Opción múltiple */}
                    {q.type === 'multiple_choice' && (
                      <div className="space-y-2">
                        {q.options.map((opt, oi) => {
                          const selected = mcqSelected[q.questionId] === oi;
                          const revealed = mcqRevealed[q.questionId];
                          const isCorrect = oi === q.correctAnswer;
                          let style = 'border-gray-200 bg-white hover:border-indigo-300 cursor-pointer';
                          if (revealed) {
                            if (isCorrect) style = 'border-green-400 bg-green-50';
                            else if (selected) style = 'border-red-400 bg-red-50';
                            else style = 'border-gray-100 bg-gray-50 opacity-60';
                          } else if (selected) {
                            style = 'border-indigo-400 bg-indigo-50';
                          }

                          return (
                            <button
                              key={oi}
                              disabled={revealed}
                              onClick={() => handleMCQ(q.questionId, oi, q.correctAnswer, q.points)}
                              className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm transition ${style}`}
                            >
                              <span className="font-medium text-gray-500 mr-2">
                                {['A', 'B', 'C', 'D'][oi]}.
                              </span>
                              {opt}
                            </button>
                          );
                        })}

                        {mcqRevealed[q.questionId] && (
                          <div className={`mt-3 p-4 rounded-xl text-sm ${
                            mcqSelected[q.questionId] === q.correctAnswer
                              ? 'bg-green-50 border border-green-200 text-green-800'
                              : 'bg-red-50 border border-red-200 text-red-800'
                          }`}>
                            <p className="font-semibold mb-1">
                              {mcqSelected[q.questionId] === q.correctAnswer ? '✅ ¡Correcto!' : '❌ No exactamente'}
                            </p>
                            <p>{q.explanation}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Respuesta corta */}
                    {q.type === 'short_answer' && (
                      <div>
                        {/* Pistas */}
                        {q.hints && (
                          <div className="mb-3 space-y-1">
                            {q.hints.map((hint, hi) => (
                              <div key={hi} className="flex items-start gap-1.5 text-xs text-indigo-500">
                                <span>💡</span>
                                <span>{hint}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <textarea
                          rows={4}
                          value={shortAnswers[q.questionId] || ''}
                          onChange={(e) =>
                            setShortAnswers((prev) => ({ ...prev, [q.questionId]: e.target.value }))
                          }
                          disabled={shortRevealed[q.questionId]}
                          placeholder="Escribe tu respuesta aquí..."
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none disabled:bg-gray-50"
                        />

                        {!shortRevealed[q.questionId] ? (
                          <button
                            onClick={() => handleShortReveal(q.questionId, q.points)}
                            disabled={!shortAnswers[q.questionId]?.trim()}
                            className={`mt-3 px-5 py-2 rounded-xl text-sm font-semibold text-white transition
                              bg-gradient-to-r ${subject.color} hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed`}
                          >
                            Ver respuesta sugerida
                          </button>
                        ) : (
                          <div className="mt-3 space-y-2">
                            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm">
                              <p className="font-semibold text-green-700 mb-1">✅ Respuesta correcta (referencia):</p>
                              {q.expectedAnswers.map((ans, ai) => (
                                <p key={ai} className="text-green-700">• {ans}</p>
                              ))}
                            </div>
                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm">
                              <p className="font-semibold text-blue-700 mb-1">💬 Retroalimentación:</p>
                              <p className="text-blue-700">{q.feedback.correct}</p>
                            </div>
                            <p className="text-xs text-gray-400 text-center">
                              +{q.points} pts añadidos a tu progreso
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Puntos acumulados */}
            <div className={`rounded-2xl p-5 bg-gradient-to-r ${subject.color} text-white text-center`}>
              <div className="text-3xl font-extrabold">{practicePoints} pts</div>
              <div className="text-white/75 text-sm">de 20 posibles en práctica</div>
            </div>

            <button
              onClick={() => setTab('challenge')}
              className="w-full py-4 rounded-2xl font-bold text-white text-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 transition shadow-md"
            >
              Ir al Desafío 🚀
            </button>
          </div>
        )}

        {/* ── TAB: DESAFÍO ──────────────────────────────── */}
        {tab === 'challenge' && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className={`bg-gradient-to-r ${subject.color} px-5 py-4`}>
                <h2 className="text-white font-extrabold text-lg">🚀 Desafío del mundo real</h2>
                <p className="text-white/75 text-sm mt-1">
                  Tipo: {topic.challenge.type === 'practical' ? 'Aplicación práctica' : 'Reflexión'}
                  {' · '}20 puntos
                </p>
              </div>
              <div className="px-5 py-5">
                <p className="text-gray-700 leading-relaxed mb-4">{topic.challenge.description}</p>

                {/* Rubrica */}
                {topic.challenge.rubric && (
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-4">
                    <p className="text-xs font-bold text-amber-700 mb-2">📋 Criterios de evaluación:</p>
                    {topic.challenge.rubric.map((r, ri) => (
                      <div key={ri} className="flex items-start gap-1.5 text-xs text-amber-700 mb-1">
                        <span>✓</span>
                        <span>{r}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Badges disponibles */}
                {topic.badges && topic.badges.length > 0 && (
                  <div className="flex gap-2 mb-4">
                    {topic.badges.map((badge) => (
                      <span key={badge}
                        className={`text-xs px-3 py-1 rounded-full font-medium
                          ${topicState.completed
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-400'}`}
                      >
                        {topicState.completed ? '🏅' : '🔒'} {badge}
                      </span>
                    ))}
                  </div>
                )}

                {/* Área de respuesta */}
                <textarea
                  rows={7}
                  value={challengeText}
                  onChange={(e) => setChallengeText(e.target.value)}
                  disabled={topicState.challengeSubmitted}
                  placeholder="Escribe tu respuesta aquí. Piensa con profundidad, no hay respuesta única correcta..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none disabled:bg-gray-50"
                />

                {!topicState.challengeSubmitted ? (
                  <button
                    onClick={handleChallengeSubmit}
                    disabled={!challengeText.trim() || saving}
                    className={`mt-4 w-full py-3 rounded-xl font-bold text-white transition
                      bg-gradient-to-r ${subject.color} hover:opacity-90
                      disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    {saving ? 'Guardando...' : '✅ Enviar desafío y completar tema'}
                  </button>
                ) : (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <p className="text-green-700 font-bold">🎉 ¡Desafío completado!</p>
                    <p className="text-green-600 text-sm mt-1">+20 puntos añadidos a tu progreso</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
